// Imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import Panel from '../shared-components/panel';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import { GetRecipesResponse } from '../../../../../../shareables/types/api/main/website/page/type/213/recipes/get';
import UserControlButton from '../shared-components/user-control-button';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import { PostRecipeParameters } from '../../../../../../shareables/types/api/main/website/page/type/213/recipe/post';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Select from '../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import Textarea from '../../../../../../shareables/foundation/front-end/components/forms/fields/textarea';
import Table from '../shared-components/table';
import LoadingRow from '../../../../../../shareables/foundation/front-end/components/tables/loading-row';
import RecipesRow from './row';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import EmptyStateRow from '../shared-components/table/empty-state-row';


// Define the accepted props
interface Props {
	pageID: number;
	storybookIsPanelOpen?: true;
	storybookRecipes?: GetRecipesResponse;
}


// Function component
const Recipes: React.FC<Props> = ({ pageID, storybookIsPanelOpen, storybookRecipes }) => {
	// Use state
	const [recipes, setRecipes] = useState<GetRecipesResponse | null>(storybookRecipes ?? null);
	const [isPanelOpen, setIsPanelOpen] = useState(storybookIsPanelOpen ?? false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const member = useAppSelector((state) => state.self.member);
	
	
	// Use ref
	const textInputSetters = useRef<((value: string) => void)[]>([]);
	const numberInputSetters = useRef<((value: string) => void)[]>([]);
	const selectSetter = useRef<(value: string | number) => void>();
	
	
	// Function that loads recipes
	const memoizedLoadRecipes = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetRecipesResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/213/recipes`,
				completion: (json) => {
					setRecipes(json.response);
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// Load recipes on mount
	useEffect(() => {
		memoizedLoadRecipes();
	}, [memoizedLoadRecipes]);
	
	
	// Build form options
	const formOptions: APIFormOptions<PostRecipeParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/213/recipe`,
		submitButton: {
			text: 'Post',
		},
	};
	
	
	// Function that handles closing the panel and resetting the form
	const closePanelAndResetForm = () => {
		// Close panel
		setIsPanelOpen(false);
		
		
		// Reset text based form inputs
		if (textInputSetters.current.length > 0) {
			for (const textInputSetter of textInputSetters.current) {
				textInputSetter('');
			}
		}
		
		
		// Reset number based form inputs
		if (numberInputSetters.current.length > 0) {
			for (const numberInputSetter of numberInputSetters.current) {
				numberInputSetter('0');
			}
		}
		
		
		// Reset select input
		selectSetter.current?.('MEMBER');
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Display page message
		dispatch(
			commonActions.showPageMessage({
				title: 'Success!',
				message: 'Your recipe has been posted.',
				color: 'success',
			})
		);
		
		
		// Reload recipes
		memoizedLoadRecipes();
		
		
		// Close panel and reset form
		closePanelAndResetForm();
	};
	
	
	// Determine if user can remove at least one recipe
	const displayRemoveColumn = Boolean(recipes?.some((recipe) => recipe.canDelete));
	
	
	// Return JSX
	return (
		<React.Fragment>
			<UserControlButton
				text='Add your recipe'
				opened={isPanelOpen}
				onClick={() => setIsPanelOpen(true)}
				icon={faPlus}
			/>
			
			<AnimateHeight height={isPanelOpen ? 'auto' : 0} duration={400} animateOpacity>
				<Panel closeOnClick={closePanelAndResetForm} signInText='posting a recipe'>
					<APIForm options={formOptions} onCancel={() => closePanelAndResetForm()} onCompletion={handleFormCompletion}>
						<Select
							name='identifier'
							label='Your name'
							initialValue='MEMBER'
							required
							bindSetter={(setter) => (selectSetter.current = setter)}
						>
							<option value='MEMBER'>{member?.names.member}</option>
							{member?.names.member !== member?.names.account && (
								<option value='ACCOUNT'>{member?.names.account}</option>
							)}
							<option value='FAMILY'>{member?.names.family}</option>
						</Select>
						
						<Input
							name='name'
							label='Recipe name'
							type='text'
							required
							maxLength={50}
							bindSetter={(setter) => textInputSetters.current.push(setter)}
						/>
						
						<Input
							name='prepTime'
							label='Prep time'
							note='in minutes'
							type='number'
							initialValue='0'
							min={0}
							max={65535}
							required
							bindSetter={(setter) => numberInputSetters.current.push(setter)}
						/>
						
						<Input
							name='cookTime'
							label='Cook time'
							note='in minutes'
							type='number'
							initialValue='0'
							min={0}
							max={65535}
							required
							bindSetter={(setter) => numberInputSetters.current.push(setter)}
						/>
						
						<Textarea
							name='ingredients'
							label='Ingredients'
							note='list one ingredient per line'
							rows={7}
							required
							placeholder={'2 cups of flour\n1 tablespoon salt\n1/2 cup of water\n...etc.'}
							bindSetter={(setter) => textInputSetters.current.push(setter)}
						/>
						
						<Textarea
							name='directions'
							label='Directions'
							rows={14}
							required
							placeholder='Explain how to prepare and/or cook your delicious recipe.'
							bindSetter={(setter) => textInputSetters.current.push(setter)}
						/>
					</APIForm>
				</Panel>
			</AnimateHeight>
			
			<Table>
				<thead>
					<tr>
						<th>Recipe</th>
						<th>Total time</th>
						{displayRemoveColumn && <th style={{ textAlign: 'center' }}>Remove</th>}
					</tr>
				</thead>
				
				<tbody>
					{recipes?.map((recipe) => (
						<RecipesRow
							key={recipe.id}
							recipe={recipe}
							pageID={pageID}
							onChange={memoizedLoadRecipes}
							displayRemoveCell={displayRemoveColumn}
						/>
					))}
					
					{recipes?.length === 0 && <EmptyStateRow>No recipes posted yet…</EmptyStateRow>}
					
					{!recipes && <LoadingRow />}
				</tbody>
			</Table>
		</React.Fragment>
	);
};

export default Recipes;
